import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import submit from "submit-form";
import useMirrorScrollbar from "../../../../hooks/useMirrorScrollbar";
import {
  chatAction,
  formatWhen,
  initials,
  logHref,
  memberHref,
  statusChip,
  timeLeft,
} from "../../../../utils/automation-activity";
import { valueNames } from "./FilterChips";

const STATS = [
  ["entered", "info"],
  ["emailsSent", "success"],
  ["waiting", "warning"],
  ["exitedEarly", "danger"],
  ["skipped", "muted"],
];
const FILTERS = ["", "sent", "waiting", "exited", "skipped"];
const COLUMNS = [
  { key: "member", sort: "member" },
  { key: "step" },
  { key: "status", sort: "status" },
  { key: "when", sort: "when" },
  { key: "action" },
];

const openPerson = (id) =>
  submit("/actions/refresh?", { method: "GET", body: { person: id } });

const useActivityText = () => {
  const { t, i18n } = useTranslation("coaching-portal");
  const tr = (key, params) => t(`emailAutomations.activity.${key}`, params);
  const when = (value) =>
    formatWhen(
      value,
      { today: tr("today"), yesterday: tr("yesterday") },
      { locale: i18n.language }
    );
  return { t, tr, when };
};

// "Membership is 24/7 Yearly and location is City Center": a Filter step as one line of text
const filterText = (filters, options, t) =>
  Object.entries(filters ?? {})
    .map(([field, values], position) => {
      const label = t(`emailAutomations.builder.filter.fields.${field}`);
      const is = t("emailAutomations.builder.filter.is", { field: label });
      const text = position > 0 ? is.charAt(0).toLowerCase() + is.slice(1) : is;
      const names = valueNames(field, values, options, t).join(", ");
      return `${position > 0 ? `${t("emailAutomations.builder.filter.and")} ` : ""}${text} ${names}`;
    })
    .join(" ");

/** The two lines of the Step column: what the step is and what happened there. */
export const StepCell = ({ row, options }) => {
  const { t, tr } = useActivityText();
  const { step } = row;
  if (!step) return null;
  const unitText = (count, unit) => tr(`units.${unit}`, { count });

  if (step.type === "email") {
    return (
      <>
        <strong>{step.subject || tr("noSubject")}</strong>
        <small>{tr("step.email")}</small>
      </>
    );
  }
  if (step.type === "delay") {
    const left = row.nextRunAt ? timeLeft(row.nextRunAt) : null;
    return (
      <>
        <strong>
          {tr("step.delay", {
            amount: step.amount,
            unit: unitText(step.amount, step.unit),
          })}
        </strong>
        <small>
          {left
            ? tr("step.delayLeft", {
                amount: left.count,
                unit: unitText(left.count, left.unit),
              })
            : tr("step.delay", {
                amount: step.amount,
                unit: unitText(step.amount, step.unit),
              })}
        </small>
      </>
    );
  }
  if (step.type === "filter") {
    return (
      <>
        <strong>{filterText(step.filters, options, t)}</strong>
        <small>{tr("step.filter")}</small>
      </>
    );
  }
  if (step.type === "exit_rule") {
    return (
      <>
        <strong>{tr("step.exitRules")}</strong>
        <small>
          {tr(`exitEvents.${step.event}`, { defaultValue: step.event })}
        </small>
      </>
    );
  }
  return (
    <>
      <strong>{tr("step.exit")}</strong>
      <small>
        {tr(`exitReasons.${step.reason}`, { defaultValue: step.reason })}
      </small>
    </>
  );
};

StepCell.propTypes = {
  row: PropTypes.object.isRequired,
  options: PropTypes.object,
};

export const StatusChip = ({ row }) => {
  const { tr } = useActivityText();
  const chip = statusChip(row);
  const reason = chip.reason
    ? tr(`skipReasons.${chip.reason}`, { defaultValue: chip.reason })
    : null;
  return (
    <span className={`al-chip al-chip--${chip.kind}`}>
      {tr(`status.${chip.label}`)}
      {reason ? ` · ${reason}` : ""}
    </span>
  );
};

StatusChip.propTypes = { row: PropTypes.object.isRequired };

const Avatar = ({ person }) => (
  <span className="al-avatar" aria-hidden="true">
    {initials(person)}
  </span>
);

Avatar.propTypes = { person: PropTypes.object.isRequired };

export const Stats = ({ stats }) => {
  const { tr } = useActivityText();
  return (
    <div className="al-stats">
      {STATS.map(([key, tone]) => (
        <div key={key} className={`al-stat al-stat--${tone}`}>
          <strong>{stats[key] ?? 0}</strong>
          <span>{tr(`stats.${key}`)}</span>
        </div>
      ))}
    </div>
  );
};

Stats.propTypes = { stats: PropTypes.object.isRequired };

const RowActions = ({ row, automationId, product }) => {
  const { tr } = useActivityText();
  const { userId } = row.person;
  return (
    <span className="al-actions">
      {userId ? (
        <a
          href={memberHref(userId, product)}
          aria-label={tr("actions.profile")}
          data-tooltip-id="al-tip"
          data-tooltip-content={tr("actions.profile")}
        >
          <img src="/img/new-icons/user-circle-light.svg" alt="" />
        </a>
      ) : (
        <span aria-hidden="true" className="al-actions__off" />
      )}
      {userId ? (
        <button
          type="button"
          onClick={() =>
            submit(chatAction(userId), { method: "POST", body: {} })
          }
          aria-label={tr("actions.message")}
          data-tooltip-id="al-tip"
          data-tooltip-content={tr("actions.message")}
        >
          <img src="/img/new-icons/chat-light.svg" alt="" />
        </button>
      ) : (
        <span aria-hidden="true" className="al-actions__off" />
      )}
      <button
        type="button"
        className="al-actions__remove"
        aria-label={tr("actions.remove")}
        data-tooltip-id="al-tip"
        data-tooltip-content={tr("actions.remove")}
        aria-disabled={!row.inside}
        onClick={() =>
          row.inside &&
          submit(
            `/actions/automations/${automationId}/activity/${row.id}/remove`,
            {
              method: "POST",
              body: {},
            }
          )
        }
      >
        <img src="/img/new-icons/arrow-right-from-line.svg" alt="" />
      </button>
    </span>
  );
};

RowActions.propTypes = {
  row: PropTypes.object.isRequired,
  automationId: PropTypes.string.isRequired,
  product: PropTypes.string,
};

const Toolbar = ({ automationId, query, disabled }) => {
  const { tr } = useActivityText();
  const status = query.status ?? "";
  return (
    <div className="al-toolbar" data-disabled={disabled ? "" : undefined}>
      <div className="al-filters">
        {FILTERS.map((filter) => (
          <a
            key={filter || "all"}
            href={
              disabled
                ? undefined
                : logHref(query, { status: filter, page: "" })
            }
            aria-disabled={disabled || undefined}
            data-current={status === filter ? "" : undefined}
          >
            {tr(`filters.${filter || "all"}`)}
          </a>
        ))}
      </div>
      <form
        className="al-search"
        action={`/coaching-portal/custom-workflows/automations/${automationId}`}
        method="GET"
      >
        <input type="hidden" name="tab" value="activity" />
        <input type="hidden" name="status" value={status} />
        <input type="hidden" name="sort" value={query.sort ?? ""} />
        <input type="hidden" name="dir" value={query.dir ?? ""} />
        <input
          type="search"
          name="search"
          defaultValue={query.search ?? ""}
          placeholder={tr("search")}
          aria-label={tr("search")}
          disabled={disabled}
        />
      </form>
    </div>
  );
};

Toolbar.propTypes = {
  automationId: PropTypes.string.isRequired,
  query: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

const SortHeader = ({ column, query }) => {
  const { tr } = useActivityText();
  const label = tr(`columns.${column.key}`);
  if (!column.sort) return <th scope="col">{label}</th>;
  const active = (query.sort || "when") === column.sort;
  const nextDir = active && (query.dir || "desc") === "asc" ? "desc" : "asc";
  return (
    <th
      scope="col"
      aria-sort={
        active
          ? (query.dir || "desc") === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <a href={logHref(query, { sort: column.sort, dir: nextDir, page: "" })}>
        {label}
        <img src="/img/new-icons/arrows-up-down.svg" alt="" />
      </a>
    </th>
  );
};

SortHeader.propTypes = {
  column: PropTypes.object.isRequired,
  query: PropTypes.object.isRequired,
};

const EmptyState = ({ filtered }) => {
  const { tr } = useActivityText();
  return (
    <div className="al-empty">
      <span className="al-empty__icon" aria-hidden="true">
        <img src="/img/new-icons/activity-line.svg" alt="" />
      </span>
      <strong>{tr(filtered ? "noMatches.title" : "empty.title")}</strong>
      <p>{tr(filtered ? "noMatches.body" : "empty.body")}</p>
    </div>
  );
};

EmptyState.propTypes = { filtered: PropTypes.bool };

const Pager = ({ activity, query }) => {
  const { tr } = useActivityText();
  if (activity.pages <= 1) return null;
  const go = (page) => logHref(query, { page: String(page) });
  return (
    <nav className="al-pager" aria-label={tr("pager")}>
      {activity.page > 1 ? (
        <a href={go(activity.page - 1)}>
          <img
            src="/img/new-icons/arrow-forward.svg"
            alt=""
            className="al-pager__back"
          />
          {tr("previous")}
        </a>
      ) : (
        <span />
      )}
      <span>
        {tr("pageOf", { page: activity.page, pages: activity.pages })}
      </span>
      {activity.page < activity.pages ? (
        <a href={go(activity.page + 1)}>
          {tr("next")}
          <img src="/img/new-icons/arrow-forward.svg" alt="" />
        </a>
      ) : (
        <span />
      )}
    </nav>
  );
};

Pager.propTypes = {
  activity: PropTypes.object.isRequired,
  query: PropTypes.object.isRequired,
};

/** The Activity log tab: what happened to everyone who entered the automation in the last 30 days. */
const ActivityLog = ({ automationId, product, activity, options, query }) => {
  const { tr, when } = useActivityText();
  const filtered = Boolean(query.status || query.search);
  const {
    scrollRef,
    mirrorRef,
    needsScroll,
    scrollWidth,
    onScrollRef,
    onScrollMirror,
  } = useMirrorScrollbar([activity.people]);
  return (
    <section className="al">
      <Tooltip id="al-tip" className="React-Tooltip custom-style z-50" />
      <header>
        <h3>{tr("title")}</h3>
        <p>{tr("subtitle")}</p>
      </header>
      <Stats stats={activity.stats} />
      <div className="al-panel">
        <Toolbar
          automationId={automationId}
          query={query}
          disabled={activity.people.length === 0 && !filtered}
        />
        <div className="al-scroll" ref={scrollRef} onScroll={onScrollRef}>
          <table className="al-table">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <SortHeader key={column.key} column={column} query={query} />
                ))}
              </tr>
            </thead>
            <tbody>
              {activity.people.map((row) => (
                <tr
                  key={row.id}
                  data-selected={query.person === row.id ? "" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="al-member"
                      onClick={() => openPerson(row.id)}
                    >
                      <Avatar person={row.person} />
                      <span>
                        <strong>{row.person.name || row.person.email}</strong>
                        {row.person.name ? (
                          <small>{row.person.email}</small>
                        ) : null}
                      </span>
                    </button>
                  </td>
                  <td className="al-step">
                    <StepCell row={row} options={options} />
                  </td>
                  <td>
                    <StatusChip row={row} />
                  </td>
                  <td>{when(row.at)}</td>
                  <td>
                    <RowActions
                      row={row}
                      automationId={automationId}
                      product={product}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {activity.people.length === 0 ? (
          <EmptyState filtered={filtered} />
        ) : null}
        <Pager activity={activity} query={query} />
      </div>
      {needsScroll && (
        <div className="al-mirror" ref={mirrorRef} onScroll={onScrollMirror}>
          <div className="al-mirror__spacer" style={{ width: scrollWidth }} />
        </div>
      )}
    </section>
  );
};

ActivityLog.propTypes = {
  automationId: PropTypes.string.isRequired,
  product: PropTypes.string,
  activity: PropTypes.shape({
    stats: PropTypes.object.isRequired,
    people: PropTypes.array.isRequired,
    page: PropTypes.number,
    pages: PropTypes.number,
  }).isRequired,
  options: PropTypes.object,
  query: PropTypes.object.isRequired,
};

export default ActivityLog;
